// src/views/controlId/ControlIdView.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import controlIdService from "../../services/controlIdService";
import { useAuth } from "../../contexts/AuthContext";
import { getApiBaseUrl } from "../../services/apiBase";

const fmtDt = (d) => {
  try { if (!d) return "—"; const s = String(d).includes("T") ? d : d + " 00:00:00"; return new Date(s).toLocaleString("pt-BR"); }
  catch { return d || "—"; }
};

const csvEscape = (v) => {
  const s = v == null ? "" : String(v);
  return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const baixarCSV = (filename, header, rows) => {
  const lines = [header.map(csvEscape).join(";"), ...rows.map((r) => r.map(csvEscape).join(";"))];
  const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
};

// ── Banner reutilizável ───────────────────────────────────────────────────
function Banner({ tipo = "info", children, onClose }) {
  const cores = {
    error:   { bg: "#FEE2E2", border: "#FECACA", color: "#991B1B", icon: "bi-exclamation-octagon" },
    success: { bg: "#DCFCE7", border: "#BBF7D0", color: "#166534", icon: "bi-check-circle" },
    info:    { bg: "#EFF6FF", border: "#BFDBFE", color: "#1D4ED8", icon: "bi-info-circle" },
    warning: { bg: "#FEF3C7", border: "#FDE68A", color: "#92400E", icon: "bi-exclamation-triangle" },
  };
  const c = cores[tipo] || cores.info;
  if (!children) return null;
  return (
    <div style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.color, borderRadius: 8,
      padding: "10px 14px", marginBottom: 12, display: "flex", alignItems: "center", gap: 10, fontSize: 13.5,
    }}>
      <i className={`bi ${c.icon}`} style={{ fontSize: 18 }} />
      <div style={{ flex: 1 }}>{children}</div>
      {onClose && (
        <button className="btn btn-sm" style={{ padding: "2px 8px", background: "transparent", color: c.color }} onClick={onClose}>
          <i className="bi bi-x-lg" />
        </button>
      )}
    </div>
  );
}

// ── Modal genérico ─────────────────────────────────────────────────────────
function ConfirmModal({ titulo, mensagem, onConfirm, onCancel, perigo = false, textoOk = "Confirmar" }) {
  return (
    <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.5)" }} onClick={onCancel}>
      <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{titulo}</h5>
            <button className="btn-close" onClick={onCancel} />
          </div>
          <div className="modal-body" style={{ fontSize: 14 }}>{mensagem}</div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onCancel}>Cancelar</button>
            <button className={`btn ${perigo ? "btn-danger" : "btn-primary"}`} onClick={onConfirm}>{textoOk}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ControlIdView() {
  const { isAdmin, isSindico, condominioAtivo, condominiosDisponiveis, podeEditar } = useAuth();
  const podeConfigurar = isAdmin; // só Admin altera config
  const podeEditarRecursos = isAdmin || isSindico || podeEditar("controlId");

  const [aba, setAba] = useState("dispositivos");
  const [idCondo, setIdCondo] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState("");
  const [pageSuccess, setPageSuccess] = useState("");
  const [pageInfo, setPageInfo] = useState("");

  // ── Config ────────────────────────────────────────────────────────────
  const [config, setConfig] = useState({
    urlApi: "", apiKey: "", apiKeyHint: "", temApiKey: false,
    ambiente: "sandbox", ativo: false, statusConexao: "NaoTestado",
    webhookSecret: "",
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [testando, setTestando] = useState(false);

  // ── Dispositivos ──────────────────────────────────────────────────────
  const [dispositivos, setDispositivos] = useState([]);
  const [showFormDisp, setShowFormDisp] = useState(false);
  const [formDisp, setFormDisp] = useState({ nmDispositivo: "", localInstalacao: "", tpAcesso: "EntradaSaida", idExterno: "", ativo: true });
  const [editDisp, setEditDisp] = useState(null);
  const [confirmDeleteDisp, setConfirmDeleteDisp] = useState(null);

  // ── Pessoas ───────────────────────────────────────────────────────────
  const [pessoas, setPessoas] = useState([]);
  const [filtroSync, setFiltroSync] = useState("");
  const [filtroBuscaPessoa, setFiltroBuscaPessoa] = useState("");
  const [showFormPessoa, setShowFormPessoa] = useState(false);
  const [formPessoa, setFormPessoa] = useState({ nmPessoa: "", cpf: "", tpPessoa: "Morador", urlFoto: "" });
  const [editPessoa, setEditPessoa] = useState(null);
  const [confirmDeletePessoa, setConfirmDeletePessoa] = useState(null);
  const [confirmImportar, setConfirmImportar] = useState(false);
  const [confirmSincTodos, setConfirmSincTodos] = useState(false);

  // ── Logs ──────────────────────────────────────────────────────────────
  const [logs, setLogs] = useState([]);
  const [logFiltros, setLogFiltros] = useState({ dtIni: "", dtFim: "", tpEvento: "", busca: "" });
  const [logPage, setLogPage] = useState(1);
  const [logLimit] = useState(50);
  const [logTotal, setLogTotal] = useState(0);
  const [logTotalPages, setLogTotalPages] = useState(1);

  // ── Listas de condomínios visíveis ───────────────────────────────────
  const condominiosLista = useMemo(() => condominiosDisponiveis || [], [condominiosDisponiveis]);

  useEffect(() => {
    if (condominioAtivo && !idCondo) setIdCondo(String(condominioAtivo.id));
  }, [condominioAtivo, idCondo]);

  const showError   = (msg) => { setPageError(msg);   setPageSuccess(""); setPageInfo(""); };
  const showSuccess = (msg) => { setPageSuccess(msg); setPageError("");   setPageInfo(""); };
  const showInfo    = (msg) => { setPageInfo(msg);    setPageError("");   setPageSuccess(""); };

  // ── Carregar dados da aba ──────────────────────────────────────────────
  const carregarAba = useCallback(async () => {
    if (!idCondo) return;
    setLoading(true);
    try {
      if (aba === "config" && podeConfigurar) {
        const r = await controlIdService.buscarConfig(idCondo);
        if (r.data) setConfig({
          urlApi: r.data.urlApi || "",
          apiKey: "",
          apiKeyHint: r.data.apiKeyHint || "",
          temApiKey: !!r.data.temApiKey,
          ambiente: r.data.ambiente || "sandbox",
          ativo: !!r.data.ativo,
          statusConexao: r.data.statusConexao || "NaoTestado",
          webhookSecret: r.data.webhookSecret || "",
        });
      } else if (aba === "dispositivos") {
        const r = await controlIdService.listarDispositivos(idCondo);
        setDispositivos(r.data || []);
      } else if (aba === "pessoas") {
        const r = await controlIdService.listarPessoas({
          idCondominio: idCondo, statusSync: filtroSync, busca: filtroBuscaPessoa,
        });
        setPessoas(r.data || []);
      } else if (aba === "logs") {
        const r = await controlIdService.listarLogs({
          idCondominio: idCondo, ...logFiltros, page: logPage, limit: logLimit,
        });
        setLogs(r.data || []); setLogTotal(r.total || 0); setLogTotalPages(r.totalPages || 1);
      }
    } catch (e) { showError(e.message); }
    setLoading(false);
  }, [idCondo, aba, filtroSync, filtroBuscaPessoa, logFiltros, logPage, logLimit, podeConfigurar]);

  useEffect(() => { if (idCondo) carregarAba(); /* eslint-disable-next-line */ }, [idCondo, aba, logPage]);

  // ── Config handlers ────────────────────────────────────────────────────
  const handleSalvarConfig = async () => {
    if (!config.urlApi.trim()) return showError("Informe a URL da API.");
    try {
      await controlIdService.salvarConfig({
        idCondominio: parseInt(idCondo),
        urlApi: config.urlApi.trim(),
        apiKey: config.apiKey || undefined, // não envia se vazio (preserva)
        ambiente: config.ambiente,
        ativo: !!config.ativo,
      });
      showSuccess("Configuração salva com sucesso!");
      setConfig((c) => ({ ...c, apiKey: "" }));
      carregarAba();
    } catch (e) { showError(e.message); }
  };

  const handleTestar = async () => {
    setTestando(true);
    try {
      const r = await controlIdService.testarConexao(idCondo);
      if (r.success) showSuccess(r.message); else showError(r.message);
      carregarAba();
    } catch (e) { showError(e.message); }
    setTestando(false);
  };

  const handleRegenerarSecret = async () => {
    try {
      const r = await controlIdService.regenerarSecret(idCondo);
      showSuccess("Webhook secret regenerado! Atualize a configuração no ControlID.");
      setConfig((c) => ({ ...c, webhookSecret: r.webhookSecret || "" }));
    } catch (e) { showError(e.message); }
  };

  const copiarClipboard = async (txt, label = "Copiado!") => {
    try { await navigator.clipboard.writeText(txt); showInfo(label); }
    catch { showError("Falha ao copiar para a área de transferência."); }
  };

  // ── Dispositivos handlers ──────────────────────────────────────────────
  const abrirNovoDisp = () => {
    setEditDisp(null);
    setFormDisp({ nmDispositivo: "", localInstalacao: "", tpAcesso: "EntradaSaida", idExterno: "", ativo: true });
    setShowFormDisp(true);
  };
  const abrirEditarDisp = (d) => {
    setEditDisp(d);
    setFormDisp({
      nmDispositivo: d.nmDispositivo || "",
      localInstalacao: d.localInstalacao || "",
      tpAcesso: d.tpAcesso || "EntradaSaida",
      idExterno: d.idExterno || "",
      ativo: !!d.ativo,
    });
    setShowFormDisp(true);
  };
  const handleSalvarDisp = async () => {
    if (!formDisp.nmDispositivo.trim()) return showError("Informe o nome do dispositivo.");
    try {
      const data = { ...formDisp, idCondominio: parseInt(idCondo) };
      if (editDisp) await controlIdService.editarDispositivo(editDisp.id, data);
      else          await controlIdService.criarDispositivo(data);
      setShowFormDisp(false); setEditDisp(null);
      showSuccess(editDisp ? "Dispositivo atualizado!" : "Dispositivo criado!");
      carregarAba();
    } catch (e) { showError(e.message); }
  };
  const handleExcluirDisp = async () => {
    if (!confirmDeleteDisp) return;
    try {
      await controlIdService.excluirDispositivo(confirmDeleteDisp.id);
      setConfirmDeleteDisp(null);
      showSuccess("Dispositivo excluído!");
      carregarAba();
    } catch (e) { showError(e.message); setConfirmDeleteDisp(null); }
  };
  const handleSincDisp = async () => {
    try {
      const r = await controlIdService.sincDisp(idCondo);
      if (r.success) showSuccess(`${r.sincronizados || 0} dispositivo(s) sincronizado(s) do ControlID.`);
      else showError(r.message || "Falha na sincronização.");
      carregarAba();
    } catch (e) { showError(e.message); }
  };

  // ── Pessoas handlers ──────────────────────────────────────────────────
  const abrirNovaPessoa = () => {
    setEditPessoa(null);
    setFormPessoa({ nmPessoa: "", cpf: "", tpPessoa: "Morador", urlFoto: "" });
    setShowFormPessoa(true);
  };
  const abrirEditarPessoa = (p) => {
    setEditPessoa(p);
    setFormPessoa({
      nmPessoa: p.nmPessoa || "",
      cpf: p.cpf || "",
      tpPessoa: p.tpPessoa || "Morador",
      urlFoto: p.urlFoto || "",
    });
    setShowFormPessoa(true);
  };
  const handleSalvarPessoa = async () => {
    if (!formPessoa.nmPessoa.trim()) return showError("Informe o nome da pessoa.");
    try {
      const data = { ...formPessoa, idCondominio: parseInt(idCondo) };
      if (editPessoa) await controlIdService.editarPessoa(editPessoa.id, data);
      else            await controlIdService.criarPessoa(data);
      setShowFormPessoa(false); setEditPessoa(null);
      showSuccess(editPessoa ? "Pessoa atualizada!" : "Pessoa criada!");
      carregarAba();
    } catch (e) { showError(e.message); }
  };
  const handleExcluirPessoa = async () => {
    if (!confirmDeletePessoa) return;
    try {
      await controlIdService.excluirPessoa(confirmDeletePessoa.id);
      setConfirmDeletePessoa(null);
      showSuccess("Pessoa excluída!");
      carregarAba();
    } catch (e) { showError(e.message); setConfirmDeletePessoa(null); }
  };
  const handleImportarMoradores = async () => {
    setConfirmImportar(false);
    try {
      const r = await controlIdService.importarMoradores(idCondo);
      showSuccess(r.message);
      carregarAba();
    } catch (e) { showError(e.message); }
  };
  const handleSincPessoa = async (id) => {
    try {
      await controlIdService.sincPessoa(id, idCondo);
      showSuccess("Pessoa sincronizada com o ControlID!");
      carregarAba();
    } catch (e) { showError(e.message); }
  };
  const handleSincTodos = async () => {
    setConfirmSincTodos(false);
    try {
      const r = await controlIdService.sincTodos(idCondo);
      showSuccess(r.message);
      carregarAba();
    } catch (e) { showError(e.message); }
  };

  // ── Logs handlers ────────────────────────────────────────────────────
  const aplicarFiltrosLogs = () => { setLogPage(1); carregarAba(); };

  const exportarLogsCSV = () => {
    if (!logs.length) return showError("Sem logs para exportar.");
    const header = ["Data/Hora", "Pessoa", "Dispositivo", "Local", "Evento", "Sentido"];
    const rows = logs.map((l) => [
      fmtDt(l.dtEvento), l.nmPessoa || "—", l.nmDispositivo || l.nmDisp || "—",
      l.localInstalacao || "—", l.tpEvento, l.tpSentido,
    ]);
    baixarCSV(`controlid_logs_${idCondo}_${new Date().toISOString().slice(0, 10)}.csv`, header, rows);
  };

  const exportarPessoasCSV = () => {
    if (!pessoas.length) return showError("Sem pessoas para exportar.");
    const header = ["Nome", "CPF", "Tipo", "Status Sync", "Dt Sync", "Erro"];
    const rows = pessoas.map((p) => [
      p.nmPessoa, p.cpf || "—", p.tpPessoa, p.statusSync,
      p.dtSync ? fmtDt(p.dtSync) : "—", p.msgErro || "",
    ]);
    baixarCSV(`controlid_pessoas_${idCondo}_${new Date().toISOString().slice(0, 10)}.csv`, header, rows);
  };

  // ── Webhook URL ────────────────────────────────────────────────────
  const webhookUrl = useMemo(() => {
    if (!idCondo) return "—";
    const base = getApiBaseUrl();
    let origin;
    if (base) {
      try { origin = new URL(base.startsWith("http") ? base : window.location.origin + base).origin; }
      catch { origin = window.location.origin; }
    } else {
      origin = window.location.origin.replace(/:\d+$/, ":3001");
    }
    return `${origin}/api/controlid/webhook/${idCondo}`;
  }, [idCondo]);

  // ── Estilos comuns ────────────────────────────────────────────────
  const tabStyle = (t) => ({
    padding: "8px 16px", borderRadius: "6px 6px 0 0", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
    background: aba === t ? "var(--primary)" : "var(--gray-100)",
    color: aba === t ? "#fff" : "var(--gray-600)",
  });
  const syncBadge = (s) => {
    const map = { Sincronizado: ["#166534", "#DCFCE7"], Pendente: ["#92400E", "#FEF3C7"], Erro: ["#991B1B", "#FEE2E2"] };
    const [c, b] = map[s] || ["#64748B", "#F1F5F9"];
    return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 12, background: b, color: c }}>{s}</span>;
  };
  const statusBadge = (s) => {
    const map = { Conectado: ["#166534", "#DCFCE7", "check-circle"], Erro: ["#991B1B", "#FEE2E2", "exclamation-circle"], NaoTestado: ["#92400E", "#FEF3C7", "question-circle"] };
    const [c, b, ic] = map[s] || map.NaoTestado;
    return <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 12, background: b, color: c }}>
      <i className={`bi bi-${ic} me-1`} />{s}
    </span>;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <i className="bi bi-shield-lock me-2" style={{ color: "var(--primary)", fontSize: 22 }} />
            ControlID — Controle de Acesso
          </h1>
          <p className="page-subtitle">Integração com iDAccess para controle de acesso facial</p>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {/* Seletor Condomínio */}
        <div className="form-section" style={{ marginBottom: 16 }}>
          <div className="row g-3 align-items-center">
            <div className="col-md-4">
              <label className="form-label">Condomínio</label>
              <select className="form-select" value={idCondo} onChange={(e) => setIdCondo(e.target.value)}>
                <option value="">— Selecione —</option>
                {condominiosLista.map((c) => (
                  <option key={c.id} value={c.id}>{c.nmFantasia || c.nmCondominio}</option>
                ))}
              </select>
            </div>
            {idCondo && config.statusConexao && (
              <div className="col-md-4 pt-3">{statusBadge(config.statusConexao)}</div>
            )}
          </div>
        </div>

        <Banner tipo="error"   onClose={() => setPageError("")}>{pageError}</Banner>
        <Banner tipo="success" onClose={() => setPageSuccess("")}>{pageSuccess}</Banner>
        <Banner tipo="info"    onClose={() => setPageInfo("")}>{pageInfo}</Banner>

        {!podeEditarRecursos && idCondo && (
          <Banner tipo="warning">Você está em modo somente leitura. Operações de escrita exigem perfil Admin ou Síndico.</Banner>
        )}

        {!idCondo ? (
          condominiosLista.length === 0 ? (
            <Banner tipo="warning">
              Nenhum condomínio disponível. {isAdmin ? "Cadastre um condomínio primeiro." : "Contate o administrador para liberar acesso."}
            </Banner>
          ) : (
            <div style={{ textAlign: "center", padding: 48, color: "var(--gray-400)" }}>
              <i className="bi bi-shield-lock" style={{ fontSize: 48 }} />
              <p style={{ marginTop: 16 }}>Selecione um condomínio para gerenciar o ControlID</p>
            </div>
          )
        ) : (
          <>
            {/* Tabs */}
            <div style={{ display: "flex", gap: 4, marginBottom: 0, borderBottom: "2px solid var(--primary)" }}>
              {[
                ...(podeConfigurar ? [{ id: "config", label: "Configuração", icon: "gear" }] : []),
                { id: "dispositivos", label: "Dispositivos", icon: "router" },
                { id: "pessoas",      label: "Pessoas",      icon: "people" },
                { id: "logs",         label: "Logs de Acesso", icon: "journal-text" },
              ].map((t) => (
                <button key={t.id} style={tabStyle(t.id)} onClick={() => setAba(t.id)}>
                  <i className={`bi bi-${t.icon} me-1`} />{t.label}
                </button>
              ))}
            </div>

            <div className="form-section" style={{ borderRadius: "0 8px 8px 8px", marginTop: 0 }}>

              {/* ── CONFIG ── */}
              {aba === "config" && podeConfigurar && (
                <div className="row g-3">
                  <div className="col-12">
                    <div style={{ background:"var(--bg-highlight)", borderRadius: 8, padding: "12px 16px", fontSize: 13 }}>
                      <div style={{ fontWeight: 600, marginBottom: 6 }}>
                        <i className="bi bi-link-45deg me-1" />Webhook URL para configurar no ControlID:
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <code style={{ fontSize: 11, wordBreak: "break-all", flex: 1 }}>{webhookUrl}</code>
                        <button className="btn btn-sm btn-outline-primary" onClick={() => copiarClipboard(webhookUrl, "URL copiada!")}>
                          <i className="bi bi-clipboard" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {config.webhookSecret && (
                    <div className="col-12">
                      <div style={{ background: "#FEF3C7", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>
                          <i className="bi bi-key me-1" />Webhook Secret (envie no header <code>X-Webhook-Secret</code>):
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <code style={{ fontSize: 11, wordBreak: "break-all", flex: 1 }}>{config.webhookSecret}</code>
                          <button className="btn btn-sm btn-outline-warning" onClick={() => copiarClipboard(config.webhookSecret, "Secret copiado!")}>
                            <i className="bi bi-clipboard" />
                          </button>
                          <button className="btn btn-sm btn-outline-danger" onClick={handleRegenerarSecret} title="Regenerar (invalida o atual)">
                            <i className="bi bi-arrow-clockwise" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="col-md-6">
                    <label className="form-label">URL da API ControlID *</label>
                    <input className="form-control" placeholder="http://192.168.1.100:8000"
                      value={config.urlApi || ""}
                      onChange={(e) => setConfig((c) => ({ ...c, urlApi: e.target.value }))} />
                    <small className="text-muted">IP ou hostname do servidor ControlID na rede local</small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">
                      API Key / Token
                      {config.temApiKey && <span className="ms-2 text-muted" style={{ fontSize: 11 }}>(atual termina em <code>•••{config.apiKeyHint}</code>)</span>}
                    </label>
                    <div className="input-group">
                      <input className="form-control" type={showApiKey ? "text" : "password"}
                        placeholder={config.temApiKey ? "Deixe em branco para manter atual" : "Chave de autenticação"}
                        value={config.apiKey || ""}
                        onChange={(e) => setConfig((c) => ({ ...c, apiKey: e.target.value }))} />
                      <button className="btn btn-outline-secondary" type="button" onClick={() => setShowApiKey((v) => !v)}>
                        <i className={`bi bi-${showApiKey ? "eye-slash" : "eye"}`} />
                      </button>
                    </div>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Ambiente</label>
                    <select className="form-select" value={config.ambiente || "sandbox"}
                      onChange={(e) => setConfig((c) => ({ ...c, ambiente: e.target.value }))}>
                      <option value="sandbox">Sandbox / Teste</option>
                      <option value="producao">Produção</option>
                    </select>
                  </div>

                  <div className="col-md-3 d-flex align-items-end">
                    <div className="form-check form-switch" style={{ marginBottom: 8 }}>
                      <input className="form-check-input" type="checkbox" checked={!!config.ativo}
                        onChange={(e) => setConfig((c) => ({ ...c, ativo: e.target.checked }))} />
                      <label className="form-check-label">Integração Ativa</label>
                    </div>
                  </div>

                  <div className="col-12 d-flex gap-2">
                    <button className="btn btn-primary" onClick={handleSalvarConfig}>
                      <i className="bi bi-floppy me-1" />Salvar Configuração
                    </button>
                    <button className="btn btn-outline-secondary" onClick={handleTestar} disabled={testando || !config.temApiKey}>
                      {testando ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-wifi me-1" />}
                      Testar Conexão
                    </button>
                  </div>
                </div>
              )}

              {/* ── DISPOSITIVOS ── */}
              {aba === "dispositivos" && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <span style={{ fontSize: 13, color: "var(--gray-500)" }}>
                      {dispositivos.length} dispositivo(s)
                    </span>
                    <div className="d-flex gap-2">
                      {podeEditarRecursos && (
                        <>
                          <button className="btn btn-outline-secondary btn-sm" onClick={handleSincDisp}>
                            <i className="bi bi-arrow-repeat me-1" />Sincronizar do ControlID
                          </button>
                          <button className="btn btn-primary btn-sm" onClick={abrirNovoDisp}>
                            <i className="bi bi-plus me-1" />Novo Dispositivo
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {loading ? <div className="text-center py-4"><span className="spinner-border" /></div> :
                    dispositivos.length === 0 ? (
                      <div style={{ textAlign: "center", padding: 32, color: "var(--gray-400)" }}>
                        <i className="bi bi-router" style={{ fontSize: 36 }} />
                        <p style={{ marginTop: 8 }}>Nenhum dispositivo cadastrado</p>
                      </div>
                    ) : (
                      <table className="table table-custom">
                        <thead><tr>
                          <th>Dispositivo</th><th>Local</th><th>Tipo Acesso</th><th>ID Externo</th><th>Status</th>
                          {podeEditarRecursos && <th style={{ width: 100 }}>Ações</th>}
                        </tr></thead>
                        <tbody>
                          {dispositivos.map((d) => (
                            <tr key={d.id}>
                              <td style={{ fontWeight: 500 }}>{d.nmDispositivo}</td>
                              <td>{d.localInstalacao || "—"}</td>
                              <td><span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background:"var(--bg-highlight)", color: "#1D4ED8" }}>{d.tpAcesso}</span></td>
                              <td><code style={{ fontSize: 11 }}>{d.idExterno || "—"}</code></td>
                              <td><span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background: d.ativo ? "#DCFCE7" : "#F1F5F9", color: d.ativo ? "#166534" : "#64748B" }}>{d.ativo ? "Ativo" : "Inativo"}</span></td>
                              {podeEditarRecursos && (
                                <td>
                                  <div className="table-actions">
                                    <button className="btn btn-outline-secondary btn-sm btn-icon" title="Editar" onClick={() => abrirEditarDisp(d)}>
                                      <i className="bi bi-pencil" />
                                    </button>
                                    <button className="btn btn-outline-danger btn-sm btn-icon" title="Excluir" onClick={() => setConfirmDeleteDisp(d)}>
                                      <i className="bi bi-trash" />
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                </div>
              )}

              {/* ── PESSOAS ── */}
              {aba === "pessoas" && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div className="d-flex gap-2 align-items-center flex-wrap">
                      <select className="form-select form-select-sm" style={{ width: 160 }} value={filtroSync} onChange={(e) => { setFiltroSync(e.target.value); }}>
                        <option value="">Todos os status</option>
                        <option value="Pendente">Pendente</option>
                        <option value="Sincronizado">Sincronizado</option>
                        <option value="Erro">Erro</option>
                      </select>
                      <input className="form-control form-control-sm" placeholder="Buscar nome/CPF" style={{ width: 200 }}
                        value={filtroBuscaPessoa} onChange={(e) => setFiltroBuscaPessoa(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && carregarAba()} />
                      <button className="btn btn-outline-primary btn-sm" onClick={carregarAba}><i className="bi bi-search" /></button>
                      <span style={{ fontSize: 12, color: "var(--gray-500)" }}>{pessoas.length} pessoa(s)</span>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      <button className="btn btn-outline-secondary btn-sm" onClick={exportarPessoasCSV} disabled={!pessoas.length}>
                        <i className="bi bi-file-earmark-spreadsheet me-1" />CSV
                      </button>
                      {podeEditarRecursos && (
                        <>
                          <button className="btn btn-outline-secondary btn-sm" onClick={() => setConfirmImportar(true)}>
                            <i className="bi bi-download me-1" />Importar Moradores
                          </button>
                          <button className="btn btn-outline-primary btn-sm" onClick={() => setConfirmSincTodos(true)}>
                            <i className="bi bi-arrow-repeat me-1" />Sincronizar Pendentes
                          </button>
                          <button className="btn btn-primary btn-sm" onClick={abrirNovaPessoa}>
                            <i className="bi bi-plus me-1" />Nova Pessoa
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {loading ? <div className="text-center py-4"><span className="spinner-border" /></div> :
                    pessoas.length === 0 ? (
                      <div style={{ textAlign: "center", padding: 32, color: "var(--gray-400)" }}>
                        <i className="bi bi-people" style={{ fontSize: 36 }} />
                        <p style={{ marginTop: 8 }}>Nenhuma pessoa cadastrada. {podeEditarRecursos && 'Clique em "Importar Moradores".'}</p>
                      </div>
                    ) : (
                      <table className="table table-custom">
                        <thead><tr>
                          <th>Nome</th><th>CPF</th><th>Tipo</th><th>Status Sync</th><th>Dt Sync</th><th>Erro</th>
                          {podeEditarRecursos && <th style={{ width: 130 }}>Ações</th>}
                        </tr></thead>
                        <tbody>
                          {pessoas.map((p) => (
                            <tr key={p.id}>
                              <td style={{ fontWeight: 500 }}>{p.nmPessoa}</td>
                              <td style={{ fontSize: 12, fontFamily: "monospace" }}>{p.cpf || "—"}</td>
                              <td><span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background:"var(--bg-highlight)", color: "#1D4ED8" }}>{p.tpPessoa}</span></td>
                              <td>{syncBadge(p.statusSync)}</td>
                              <td style={{ fontSize: 12 }}>{p.dtSync ? fmtDt(p.dtSync) : "—"}</td>
                              <td style={{ fontSize: 11, color: "#991B1B", maxWidth: 200 }}>{p.msgErro || ""}</td>
                              {podeEditarRecursos && (
                                <td>
                                  <div className="table-actions">
                                    <button className="btn btn-outline-primary btn-sm btn-icon" title="Sincronizar" onClick={() => handleSincPessoa(p.id)}>
                                      <i className="bi bi-arrow-repeat" />
                                    </button>
                                    <button className="btn btn-outline-secondary btn-sm btn-icon" title="Editar" onClick={() => abrirEditarPessoa(p)}>
                                      <i className="bi bi-pencil" />
                                    </button>
                                    <button className="btn btn-outline-danger btn-sm btn-icon" title="Excluir" onClick={() => setConfirmDeletePessoa(p)}>
                                      <i className="bi bi-trash" />
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                </div>
              )}

              {/* ── LOGS ── */}
              {aba === "logs" && (
                <div>
                  <div className="row g-2 mb-3 align-items-end">
                    <div className="col-md-2">
                      <label className="form-label">Data Inicial</label>
                      <input type="date" className="form-control form-control-sm" value={logFiltros.dtIni}
                        onChange={(e) => setLogFiltros((f) => ({ ...f, dtIni: e.target.value }))} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Data Final</label>
                      <input type="date" className="form-control form-control-sm" value={logFiltros.dtFim}
                        onChange={(e) => setLogFiltros((f) => ({ ...f, dtFim: e.target.value }))} />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Evento</label>
                      <select className="form-select form-select-sm" value={logFiltros.tpEvento}
                        onChange={(e) => setLogFiltros((f) => ({ ...f, tpEvento: e.target.value }))}>
                        <option value="">Todos</option>
                        <option value="Acesso">Acesso</option>
                        <option value="AcessoNegado">Acesso Negado</option>
                        <option value="Alarme">Alarme</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Buscar</label>
                      <input className="form-control form-control-sm" placeholder="Pessoa ou dispositivo"
                        value={logFiltros.busca}
                        onChange={(e) => setLogFiltros((f) => ({ ...f, busca: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && aplicarFiltrosLogs()} />
                    </div>
                    <div className="col-md-3 d-flex gap-1">
                      <button className="btn btn-primary btn-sm flex-grow-1" onClick={aplicarFiltrosLogs}>
                        <i className="bi bi-search me-1" />Filtrar
                      </button>
                      <button className="btn btn-outline-secondary btn-sm" onClick={exportarLogsCSV} disabled={!logs.length} title="Exportar CSV">
                        <i className="bi bi-file-earmark-spreadsheet" />
                      </button>
                      <button className="btn btn-outline-secondary btn-sm" onClick={() => window.print()} title="Imprimir">
                        <i className="bi bi-printer" />
                      </button>
                    </div>
                  </div>
                  <div className="text-end mb-2" style={{ fontSize: 12, color: "var(--gray-500)" }}>
                    {logTotal} registro(s) • página {logPage} de {logTotalPages}
                  </div>
                  {loading ? <div className="text-center py-4"><span className="spinner-border" /></div> :
                    logs.length === 0 ? (
                      <div style={{ textAlign: "center", padding: 32, color: "var(--gray-400)" }}>
                        <i className="bi bi-journal-text" style={{ fontSize: 36 }} />
                        <p style={{ marginTop: 8 }}>Nenhum log no período</p>
                      </div>
                    ) : (
                      <>
                        <table className="table table-custom" style={{ fontSize: 13 }}>
                          <thead><tr>
                            <th>Data/Hora</th><th>Pessoa</th><th>Dispositivo</th><th>Local</th><th>Evento</th><th>Sentido</th>
                          </tr></thead>
                          <tbody>
                            {logs.map((l) => (
                              <tr key={l.id}>
                                <td style={{ fontFamily: "monospace", fontSize: 12 }}>{fmtDt(l.dtEvento)}</td>
                                <td style={{ fontWeight: 500 }}>{l.nmPessoa || "—"}</td>
                                <td>{l.nmDispositivo || l.nmDisp || "—"}</td>
                                <td style={{ fontSize: 11, color: "var(--gray-500)" }}>{l.localInstalacao || "—"}</td>
                                <td>
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 12,
                                    background: l.tpEvento === "Acesso" ? "#DCFCE7" : l.tpEvento === "AcessoNegado" ? "#FEE2E2" : "#FEF3C7",
                                    color:      l.tpEvento === "Acesso" ? "#166534" : l.tpEvento === "AcessoNegado" ? "#991B1B" : "#92400E" }}>
                                    {l.tpEvento}
                                  </span>
                                </td>
                                <td>
                                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 12, background:"var(--bg-highlight)", color: "#1D4ED8" }}>
                                    {l.tpSentido === "Entrada" ? "Entrada" : l.tpSentido === "Saida" ? "Saída" : "—"}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {logTotalPages > 1 && (
                          <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                            <button className="btn btn-sm btn-outline-secondary" disabled={logPage <= 1} onClick={() => setLogPage(1)}>«</button>
                            <button className="btn btn-sm btn-outline-secondary" disabled={logPage <= 1} onClick={() => setLogPage((p) => Math.max(1, p - 1))}>‹</button>
                            <span style={{ fontSize: 13 }}>{logPage} / {logTotalPages}</span>
                            <button className="btn btn-sm btn-outline-secondary" disabled={logPage >= logTotalPages} onClick={() => setLogPage((p) => Math.min(logTotalPages, p + 1))}>›</button>
                            <button className="btn btn-sm btn-outline-secondary" disabled={logPage >= logTotalPages} onClick={() => setLogPage(logTotalPages)}>»</button>
                          </div>
                        )}
                      </>
                    )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modal Dispositivo */}
      {showFormDisp && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.5)" }} onClick={() => setShowFormDisp(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editDisp ? "Editar" : "Novo"} Dispositivo</h5>
                <button className="btn-close" onClick={() => setShowFormDisp(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Nome do Dispositivo *</label>
                  <input className="form-control" value={formDisp.nmDispositivo} onChange={(e) => setFormDisp((f) => ({ ...f, nmDispositivo: e.target.value }))} placeholder="Ex: Leitor Entrada Principal" />
                </div>
                <div className="mb-3">
                  <label className="form-label">Local de Instalação</label>
                  <input className="form-control" value={formDisp.localInstalacao} onChange={(e) => setFormDisp((f) => ({ ...f, localInstalacao: e.target.value }))} placeholder="Ex: Portaria, Garagem, Academia" />
                </div>
                <div className="mb-3">
                  <label className="form-label">Tipo de Acesso</label>
                  <select className="form-select" value={formDisp.tpAcesso} onChange={(e) => setFormDisp((f) => ({ ...f, tpAcesso: e.target.value }))}>
                    <option value="Entrada">Somente Entrada</option>
                    <option value="Saida">Somente Saída</option>
                    <option value="EntradaSaida">Entrada e Saída</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">ID Externo (ControlID)</label>
                  <input className="form-control" value={formDisp.idExterno} onChange={(e) => setFormDisp((f) => ({ ...f, idExterno: e.target.value }))} placeholder="Preenchido automaticamente na sincronização" />
                  <small className="text-muted">Geralmente preenchido pela sincronização automática.</small>
                </div>
                {editDisp && (
                  <div className="form-check form-switch mb-2">
                    <input className="form-check-input" type="checkbox" checked={!!formDisp.ativo} onChange={(e) => setFormDisp((f) => ({ ...f, ativo: e.target.checked }))} />
                    <label className="form-check-label">Dispositivo Ativo</label>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowFormDisp(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={handleSalvarDisp}>
                  <i className="bi bi-floppy me-1" />Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Pessoa */}
      {showFormPessoa && (
        <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,.5)" }} onClick={() => setShowFormPessoa(false)}>
          <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editPessoa ? "Editar" : "Nova"} Pessoa</h5>
                <button className="btn-close" onClick={() => setShowFormPessoa(false)} />
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Nome Completo *</label>
                  <input className="form-control" value={formPessoa.nmPessoa} onChange={(e) => setFormPessoa((f) => ({ ...f, nmPessoa: e.target.value }))} />
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-md-6">
                    <label className="form-label">CPF</label>
                    <input className="form-control" value={formPessoa.cpf} onChange={(e) => setFormPessoa((f) => ({ ...f, cpf: e.target.value }))} placeholder="Somente números" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Tipo</label>
                    <select className="form-select" value={formPessoa.tpPessoa} onChange={(e) => setFormPessoa((f) => ({ ...f, tpPessoa: e.target.value }))}>
                      <option value="Morador">Morador</option>
                      <option value="Funcionario">Funcionário</option>
                      <option value="Visitante">Visitante</option>
                      <option value="Prestador">Prestador</option>
                    </select>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">URL da Foto</label>
                  <input className="form-control" value={formPessoa.urlFoto} onChange={(e) => setFormPessoa((f) => ({ ...f, urlFoto: e.target.value }))} placeholder="https://..." />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowFormPessoa(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={handleSalvarPessoa}>
                  <i className="bi bi-floppy me-1" />Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteDisp && (
        <ConfirmModal titulo="Excluir Dispositivo" perigo textoOk="Excluir"
          mensagem={<>Tem certeza que deseja excluir o dispositivo <strong>{confirmDeleteDisp.nmDispositivo}</strong>?</>}
          onConfirm={handleExcluirDisp} onCancel={() => setConfirmDeleteDisp(null)} />
      )}

      {confirmDeletePessoa && (
        <ConfirmModal titulo="Excluir Pessoa" perigo textoOk="Excluir"
          mensagem={<>Tem certeza que deseja excluir <strong>{confirmDeletePessoa.nmPessoa}</strong>?</>}
          onConfirm={handleExcluirPessoa} onCancel={() => setConfirmDeletePessoa(null)} />
      )}

      {confirmImportar && (
        <ConfirmModal titulo="Importar Moradores" textoOk="Importar"
          mensagem="Os moradores ativos do condomínio serão criados como Pessoas pendentes de sincronização com o ControlID. Pessoas já vinculadas terão dados atualizados. Deseja continuar?"
          onConfirm={handleImportarMoradores} onCancel={() => setConfirmImportar(false)} />
      )}

      {confirmSincTodos && (
        <ConfirmModal titulo="Sincronizar Pendentes" textoOk="Sincronizar"
          mensagem="Todas as pessoas com status 'Pendente' ou 'Erro' serão enviadas ao ControlID. Esta operação pode levar alguns segundos. Deseja continuar?"
          onConfirm={handleSincTodos} onCancel={() => setConfirmSincTodos(false)} />
      )}
    </div>
  );
}
